import React, { useCallback, useState } from 'react';

import { View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useDispatch, useSelector } from 'react-redux';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import {
    SPOTLIGHT_DESCRIPTION_ID,
    SPOTLIGHT_SKIP_BUTTON_ID,
    SPOTLIGHT_TITLE_ID,
    SPOTLIGHT_WATCH_BUTTON_ID,
} from 'config/testID';

import { selectLayoutConfig } from 'store/config/selectors';
import { setSpotlightVideoVisible } from 'store/player/actions';
import { selectSpotlightVideoVisible } from 'store/player/selectors';

import { RegularStorage, StorageKeys } from 'services/mmkv';

import { COLOR } from 'utils/colors';
import useEnterTheApp from 'utils/hooks/appBootstrap/useEnterTheApp';
import { isPhone, isWeb } from 'utils/responsiveness';

import { PLAY_DETAIL } from 'screens/Detail/languageConst';
import { underlayingPlayerModalElementsDataSet } from 'screens/PlayerModal/hooks/useModalAccessibility.ts';

import ActionButton from 'components/CustomButtons/ActionButton';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import styles from './styles';

const { SKIP_BUTTON, WATCH_AGAIN_BUTTON, NEXT_BUTTON } = LANGUAGE_KEYS;

function FtiTutorial() {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { primary } = useSelector(selectLayoutConfig);
    const spotlightPlayerVisible = useSelector(selectSpotlightVideoVisible);

    const [videoWasWatched, setVideoWasWatched] = useState(false);

    const { onEnterTheApp } = useEnterTheApp();

    const handleSkipPress = useCallback(async () => {
        await RegularStorage.setItem(
            StorageKeys.TUTORIAL_PAGE_SHOWN,
            JSON.stringify(true),
        );
        await onEnterTheApp();
    }, [onEnterTheApp]);

    const handleWatchPress = useCallback(() => {
        setVideoWasWatched(true);
        dispatch(setSpotlightVideoVisible(true));
    }, [dispatch]);

    const buttonTexts = {
        primary: videoWasWatched ? t(WATCH_AGAIN_BUTTON) : t(PLAY_DETAIL),
        secondary: videoWasWatched ? t(NEXT_BUTTON) : t(SKIP_BUTTON),
    };

    return (
        <SafeAreaView
            style={styles.root}
            dataSet={underlayingPlayerModalElementsDataSet}
        >
            <View style={isPhone ? styles.phoneContent : styles.desktopContent}>
                <View>
                    <Text
                        style={styles.text}
                        category={ETextCategory.title1}
                        color={ETextColor.neutral}
                        testID={SPOTLIGHT_TITLE_ID}
                        role="heading"
                        accessibilityRole="header"
                        {...(isWeb && { tabIndex: 0 })}
                        aria-hidden={spotlightPlayerVisible}
                        accessibilityElementsHidden={spotlightPlayerVisible}
                    >
                        {t('FTI_SPOTLIGHT_VIDEO_TITLE')}
                    </Text>
                    <Text
                        style={[styles.text, styles.description]}
                        category={ETextCategory.body1}
                        color={ETextColor.light_grey}
                        testID={SPOTLIGHT_DESCRIPTION_ID}
                        {...(isWeb && { tabIndex: 0 })}
                        aria-hidden={spotlightPlayerVisible}
                        accessibilityElementsHidden={spotlightPlayerVisible}
                    >
                        {t('FTI_SPOTLIGHT_VIDEO_BODY')}
                    </Text>
                </View>
                <View style={styles.buttonsContainer}>
                    <ActionButton
                        buttonStyle={styles.button}
                        buttonTextStyle={styles.actionButtonText}
                        title={buttonTexts.primary}
                        fillColor={primary}
                        onPress={handleWatchPress}
                        testID={SPOTLIGHT_WATCH_BUTTON_ID}
                        accessibilityLabel={buttonTexts.primary}
                        accessible
                        aria-hidden={spotlightPlayerVisible}
                        accessibilityElementsHidden={spotlightPlayerVisible}
                    />
                    <ActionButton
                        buttonStyle={[
                            styles.button,
                            styles.buttonBorder,
                            styles.buttonIndent,
                        ]}
                        title={buttonTexts.secondary}
                        fillColor={COLOR.BLACK}
                        onPress={handleSkipPress}
                        testID={SPOTLIGHT_SKIP_BUTTON_ID}
                        accessibilityLabel={buttonTexts.secondary}
                        accessible
                        aria-hidden={spotlightPlayerVisible}
                        accessibilityElementsHidden={spotlightPlayerVisible}
                    />
                </View>
            </View>
        </SafeAreaView>
    );
}

export default FtiTutorial;
